/**
 * redirectUtils.js
 * Utility functions for managing redirects in the application
 */

/**
 * Map of legacy URLs to their new destinations
 * This complements the Firebase hosting redirects defined in firebase.json
 * For dynamic or parameterized redirects that can't be handled by static redirects
 */
const REDIRECT_MAP = {
  // Old blog URL format to new format
  '/blog-post-': '/blog/',
  
  // Old URL parameters format
  '/property?id=': '/contracts/',
  '/contract?id=': '/contracts/',
  
  // Legacy URL structures
  '/properties/': '/contracts/',
  '/my-contracts': '/contracts',
  '/user-profile': '/profile',
  '/admin': '/admin-panel',
  
  // Common misspellings
  '/blogs': '/blog',
  '/contrato': '/contracts',
  '/perfil': '/profile',
  
  // URL normalization (trailing slashes)
  '/blog/': '/blog',
  '/signup/': '/signup',
  '/login/': '/login',
  '/profile/': '/profile',
  '/contacto/': '/contacto'
};

/**
 * Checks if the current URL matches any redirect patterns and performs the redirect if needed
 * @returns {boolean} True if a redirect was performed, false otherwise
 */
export function checkAndApplyRedirects() {
  // First check for domain-level redirects (www -> non-www)
  if (window.location.hostname === 'www.coolliving.io') {
    const newUrl = window.location.href.replace('www.coolliving.io', 'coolliving.io');
    window.location.replace(newUrl);
    return true;
  }
  
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  
  // IMPORTANTE: No redirigir URLs de contratos específicos (/contracts/[ID])
  if (/^\/contracts\/[a-zA-Z0-9-_]+$/.test(currentPath)) {
    return false;
  }
  
  // Check for exact matches first
  if (REDIRECT_MAP[currentPath]) {
    const destination = REDIRECT_MAP[currentPath];
    window.history.replaceState(null, '', destination + currentSearch);
    return true;
  }
  
  // Check for partial matches (patterns)
  for (const [pattern, destination] of Object.entries(REDIRECT_MAP)) {
    // IMPORTANTE: No redirigir URLs de contratos específicos (/contracts/[ID])
    if (currentPath.startsWith('/contracts/') && /^\/contracts\/[a-zA-Z0-9-_]+$/.test(currentPath)) {
      continue;
    }
    
    if (pattern.includes('?') && currentPath.includes(pattern.split('?')[0])) {
      // Handle URL parameter transformations
      const paramName = pattern.split('=')[0].split('?')[1];
      const params = new URLSearchParams(currentSearch);
      const paramValue = params.get(paramName);
      
      if (paramValue) {
        window.history.replaceState(null, '', `${destination}${paramValue}`);
        return true;
      }
    } else if (currentPath.includes(pattern) && pattern !== '/') {
      // Handle path transformations for patterns
      const newPath = currentPath.replace(pattern, destination);
      window.history.replaceState(null, '', newPath + currentSearch);
      return true;
    }
  }
  
  return false;
}

/**
 * Handles external redirects (e.g., from external sites or old domains)
 * @param {string} referrer - The referring URL
 * @returns {string|null} The redirect URL or null if no redirect is needed
 */
export function handleExternalRedirects(referrer) {
  if (!referrer) return null;
  
  try {
    const referrerUrl = new URL(referrer);
    const referrerDomain = referrerUrl.hostname;
    
    // Map old domains to specific landing pages
    const domainMap = {
      'contratosano.com': '/',
      'www.contratosano.com': '/',
      'app.contratosano.com': '/dashboard',
      'blog.contratosano.com': '/blog'
    };
    
    return domainMap[referrerDomain] || null;
  } catch (e) {
    console.error('Error parsing referrer URL:', e);
    return null;
  }
}

/**
 * Setup and track URL changes for analytics
 * @param {Object} router - Vue Router instance
 */
export function setupRedirectTracking(router) {
  // Track 404 redirects
  router.onError((error) => {
    if (error.message.includes('Failed to fetch dynamically imported module')) {
      console.error('Navigation error:', error);
      
      // Could integrate with analytics service here
      // analyticsService.logEvent('navigation_error', { path: router.currentRoute.value.fullPath });
    }
  });
}

/**
 * Generates a canonical URL for the current page
 * @param {string} path - Current path
 * @returns {string} Canonical URL
 */
export function getCanonicalUrl(path) {
  const baseUrl = 'https://coolliving.io';
  
  // Remove trailing slash except for home page
  let cleanPath = path === '/' ? '/' : path.replace(/\/$/, '');
  
  // Remove query parameters
  cleanPath = cleanPath.split('?')[0];
  
  return `${baseUrl}${cleanPath}`;
} 